import { Link, LinkProps } from "@tanstack/react-router"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import * as React from "react"

export type SettingsSection = {
  id: string
  title: string
  description: string
  icon?: React.ElementType
  isActive?: boolean
  isDisabled?: boolean
  disabledReason?: string
  action?: React.ReactNode
} & (
  | {
      comingSoon: true
    }
  | {
      navigate: LinkProps
    }
  | {
      displayOnly: true
    }
)

export function SettingsCard({ section }: { section: SettingsSection }) {
  const isLink = "navigate" in section && !section.isDisabled
  const Component = isLink ? Link : "div"

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Card
          className={cn(
            "relative w-full overflow-hidden",
            isLink && "transition-all duration-75 cursor-pointer hover:bg-muted",
            ("comingSoon" in section || section.isDisabled) && "cursor-not-allowed opacity-50",
          )}
          key={section.id}
          aria-disabled={section.isDisabled}
        >
          <Component {...(isLink ? section.navigate : {})}>
            {section.icon && (
              <div className="absolute right-1/24 -bottom-10">
                <section.icon
                  className={cn("size-36 text-muted-foreground opacity-10")}
                  strokeWidth={1.25}
                />
              </div>
            )}
            <div className="flex flex-col gap-2 p-4 pb-6">
              <div className="flex items-start justify-between gap-6">
                <div className="text-lg font-semibold flex gap-2 items-center">
                  <span>{section.title}</span>
                  {section.isActive && (
                    <span>
                      <Pulse className="fill-emerald-500" />
                    </span>
                  )}
                </div>
                {section.action && <div className="shrink-0">{section.action}</div>}
              </div>
              <div className="text-base text-muted-foreground max-w-full pr-12">
                {section.description}
              </div>
            </div>
          </Component>
        </Card>
      </TooltipTrigger>
      {section.isDisabled && section.disabledReason && (
        <TooltipContent>{section.disabledReason}</TooltipContent>
      )}
    </Tooltip>
  )
}
